(ns app.domain.application.guards.party
  (:require #?(:clj [app.domain.sign-off.core :as sign-off])
            [app.domain.application.guards.utils :as guard-utils]
            [app.domain.form.model :as form-model]
            [app.domain.form.validators :as form-validators]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.domain.requirement.model :as req-model]
            [app.domain.sign-off.model :as sign-off.model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [clojure.set :as set]
            [potpuri.core :as p]
            [tilakone.core :as tk]))

(defn find-party
  ([phase id]
   (some-> phase
           party-model/phase-parties
           (p/find-first {:db/id id})))
  ([{:keys [phase party-id]}]
   (find-party phase party-id)))

(defn phase-and-party-id-from-fsm
  "Helper function to destruct (possible) party-id and phase (target) from fsm.
  The result map can be passed to `find-party`."
  [{[_ {party-id :party-id}] ::tk/signal
    {phase :target}          :request}]
  {:phase phase :party-id party-id})

(defn active-parties [fsm]
  (->> fsm
       :request
       :target
       party-model/phase-parties
       (filter party-model/active?)))

(defn authority-group [fsm]
  (-> fsm :request :target group-model/get-authority-group))

(defn required-party-roles-handler [fsm-target
                                    requirement-kinds
                                    & [hide-deferred?]]
  (let [missing-role-names (->> (party-model/missing-roles fsm-target requirement-kinds hide-deferred?)
                                (map :party-role/names))]
    (or (empty? missing-role-names)
        (throw (ex-info "Required party roles missing"
                        {:error-translation-keys missing-role-names})))))

(def required-party-roles
  {:name    :process.guard/required-party-roles
   :summary "Ensure that all the party role requirements are fulfilled.
             Requirement kinds to validate (:requirement.kind/role and/or :requirement.kind/deferred-role) can be given as guard parameters.
             If none are given, only :requirement.kind/role requirements are checked and :requirement.kind/deferred-role ignored"
   :type    :guard
   :handler (fn [{{fsm-target :target}  :request
                  [_ requirement-kinds] ::tk/guard}]
              (required-party-roles-handler fsm-target requirement-kinds))})

(def required-party-roles-except-deferred
  {:name    :process.guard/required-party-roles-except-deferred
   :summary "Ensure that all the party role requirements are fulfilled.
             Party roles required at issuance (:requirement.kind/deferred-role) have to be assigned
             or explicitly deferred (:requirement/deferred-by)"
   :type    :guard
   :handler (fn [{{fsm-target :target} :request}]
              (required-party-roles-handler fsm-target req-model/all-role-requirement-kinds true))})

(def required-party-role-trades
  {:name    :process.guard/required-party-role-trades
   :summary "Ensure that the assigned parties have registered to the trades required by their assigned party roles"
   :type    :guard
   :handler (fn [{{fsm-target :target} :request}]
              (let [roles-with-missing-trades (party-model/roles-with-unfulfilled-trade-requirement fsm-target)
                    role-names                (->> roles-with-missing-trades
                                                   (map :party-role/names))]
                (or (empty? role-names)
                    (throw (ex-info "Required party role trades missing"
                                    {:error-translation-keys role-names})))))})

(def party-role-not-required?
  {:name    :process.guard/party-role-not-required?
   :type    :guard
   :summary "Check that the target does not require the given party role"
   :handler (fn [{{:keys [target]} :request
                  [_ param-name]   ::tk/guard
                  :as              fsm}]
              (if-let [party-role-id (guard-utils/get-param fsm param-name)]
                (let [required-role-ids (->> target
                                             req-model/phase-role-requirements
                                             (map (comp :db/id :requirement/party-role))
                                             (into #{}))]
                  (not (contains? required-role-ids party-role-id)))
                true))})

(def no-user-applicant-parties
  {:name    :process.guard/no-user-applicant-parties
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (party-model/role-only-has-non-user-parties? target :role/applicant))})

(def application-has-applicant?
  {:name    :process.guard/application-has-applicant?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   active-parties
                   (some party-model/is-applicant?)))})

(defn- get-role-for-key [fsm target-role-key]
  (-> (authority-group fsm)
      :party-role/_authority-group
      (p/find-first {:party-role/key target-role-key})
      :db/id))

(defn- role-not-included-in-signal?
  "Check that the specified party-role entity is not included in the signal"
  [[_ {:keys       [role-id]
       :party/keys [party-roles]}]
   target-role-id]
  (-> (cond->> party-roles
        role-id (cons role-id))
      set
      (contains? target-role-id)
      not))

(defn- parties-do-not-have-role?
  "Check that none of the given existing parties have the specified party-role (identified by role key)"
  [target-role-key parties]
  (->> parties
       (mapcat :party/party-roles)
       (map :party-role/key)
       set
       target-role-key
       not))

(def single-party-of-type
  {:name    :process.guard/single-party-of-type
   :type    :guard
   :summary "Ensure that only a single party can have a specific role"
   :handler (fn [{signal              ::tk/signal
                  [_ target-role-key] ::tk/guard
                  :as                 fsm}]
              (let [target-role-id (get-role-for-key fsm target-role-key)]
                (or (role-not-included-in-signal? signal target-role-id)
                    ;; ... or does not already exist
                    (->> fsm
                         active-parties
                         (parties-do-not-have-role? target-role-key)))))})

(def single-business-party-of-type
  {:name    :process.guard/single-business-party-of-type
   :type    :guard
   :summary (str "Ensure that only a single party can both a business and have a specific role. "
                 "Having user parties with the same role is allowed.")
   :handler (fn [{signal              ::tk/signal
                  [_ target-role-key] ::tk/guard
                  :as                 fsm}]
              (let [target-role-id (get-role-for-key fsm target-role-key)]
                (or (role-not-included-in-signal? signal target-role-id)
                    ;; ... or does not already exist
                    (->> fsm
                         active-parties
                         (filter :party/business)
                         (parties-do-not-have-role? target-role-key)))))})

(def unique-party-email?
  {:name    :process.guard/unique-party-email?
   :type    :guard
   :handler (fn [{[_ params]       ::tk/signal
                  {:keys [target]} :request
                  :as              fsm}]
              (if-let [new-email (or (:party/email params)
                                     (let [{:keys [new-value party-id field-path]} params]
                                       (and (-> field-path (= :party/email))
                                            (not= new-value
                                                  (->> (find-party target party-id)
                                                       party-model/get-email))
                                            new-value)))]
                (-> (->> fsm
                         :request
                         :target
                         party-model/phase-parties
                         (filter (du/in-state-fn :party.state/active
                                                 :party.state/pending))
                         (keep party-model/get-email)
                         set)
                    (contains? new-email)
                    not)
                true))})

(def unique-business?
  {:name    :process.guard/unique-business?
   :type    :guard
   :handler (fn [{[_ {:business/keys [unique-id]}] ::tk/signal
                  {:keys [target]}                 :request}]
              (-> (->> target
                       party-model/phase-parties
                       (keep :party/business)
                       (map :business/unique-id)
                       set)
                  (contains? unique-id)
                  not))})

(def party-has-role?
  {:name    :process.guard/party-has-role?
   :type    :guard
   :handler (fn [{[_ role-key] ::tk/guard :as fsm}]
              (-> fsm phase-and-party-id-from-fsm find-party (party-model/has-role? role-key)))})

(def party-has-other-roles?
  {:name    :process.guard/party-has-other-roles?
   :type    :guard
   :handler (fn [{[_ {:keys [role-id]}] ::tk/signal :as fsm}]
              (-> fsm
                  phase-and-party-id-from-fsm
                  find-party
                  :party/party-roles
                  (->> (map :db/id))
                  set
                  (disj role-id)
                  seq))})

(def party-has-no-sacred-roles?
  {:name    :process.guard/party-has-no-sacred-roles?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   phase-and-party-id-from-fsm
                   find-party
                   (party-model/has-any-role? sign-off.model/sacred-role-keys)
                   not))})

(def party-has-email-but-no-user?
  {:name    :process.guard/party-has-email-but-no-user?
   :type    :guard
   :handler (fn [fsm]
              (if-let [party-id (-> fsm :tilakone.core/signal second :party-id)]
                (let [target (-> fsm :request :target)
                      party  (find-party target party-id)]
                  (and (:party/email party)
                       (nil? (:party/user party))))
                false))})

(def party-has-user-without-integration?
  {:name    :process.guard/party-has-no-integration?
   :type    :guard
   :handler (fn [fsm]
              (if-let [party-id (-> fsm :tilakone.core/signal second :party-id)]
                (let [phase    (-> fsm :request :target)
                      party    (find-party phase party-id)
                      user     (-> party :party/user)
                      group-id (-> phase :entity/parent :domain/authority-group :group/id)]
                  (or (and (nil? user)
                           (-> party :user.integration/user-id nil?))
                      (and (some? user)
                           (->> user
                                :user/integrations
                                (not-any? #(-> % :user.integration/customer-id (= group-id)))))))
                false))})

(defn find-ws-sof [phase-entity]
  (->> phase-entity
       :entity/parent
       ;; find sign-off-form regardless of target
       workspace-model/workspace-application
       :application/sign-off-form))

(defn sign-off-required-role-keys [fsm]
  #?(:clj  (sign-off/required-role-keys-at-sign-off-time (-> fsm :request :target))
     :cljs (or
             ;; backend-sent data based on configuration at the time of actual sign-off
             (-> fsm :view-data :required-so-roles)
             ;; fall back to reading current configuration as it exists in the frontend app-db
             (-> fsm
                 :request
                 :target
                 :entity/parent
                 workspace-model/default-config-path
                 sign-off.model/required-sign-off-role-keys))))

(def ^:private get-form-elements #?(:clj  form-model/get-form-elements
                                    :cljs (memoize form-model/get-form-elements)))

(defn form-sign-off-parties [sign-off-form]
  (->> sign-off-form
       form-model/get-form-data-schema
       get-form-elements
       (some #(= (first %) :sign-off/parties))))

(defn locked-role-keys [sign-off-role-keys sign-off-form]
  (set/union sign-off-role-keys
             (when (form-sign-off-parties sign-off-form)
               sign-off.model/locked-role-keys)))

(defn- get-role-key-fn [fsm]
  (let [party-roles (-> fsm authority-group :party-role/_authority-group)]
    (fn [role-id]
      (-> party-roles (p/find-first {:db/id role-id}) :party-role/key))))

(defn role-keys-from-fsm-role-id [{[_ params] ::tk/signal :as fsm}]
  (let [get-role-key-by-role-id (get-role-key-fn fsm)]
    (concat
      ;; disallow if edit attempts to add or remove locked role,
      ;; or if signal tries to add a requirement for a locked role
      (-> params :role-id get-role-key-by-role-id list)
      ;; ... or if signal tries to invite or add a party with a locked role
      (->> params :party/party-roles (map get-role-key-by-role-id)))))

(defn check-locked-party-when-signed-off
  ([fsm role-keys unrestricted-roles]
   (let [target             (-> fsm :request :target)
         sign-off-role-keys (-> fsm sign-off-required-role-keys set)
         sof                (find-ws-sof target)
         locked-roles-keys  (set/difference (locked-role-keys sign-off-role-keys sof)
                                            unrestricted-roles)]
     (not (sign-off.model/party-roles-locked? sof role-keys locked-roles-keys))))
  ([fsm role-keys]
   (check-locked-party-when-signed-off fsm role-keys nil)))

(def locked-party-details-when-signed-off
  {:name    :process.guard/locked-party-details-when-signed-off
   :type    :guard
   :summary "Returns false if the target party currently has some of the sign off roles.
             Used to block editing party details and deleting the party."
   :handler (fn [fsm]
              (or
                ;; allow editing email - this way, user can be invited
                (-> fsm ::tk/signal second :field-path (= :party/email))
                ;; for other attributes, disallow if signed off
                (check-locked-party-when-signed-off
                  fsm (-> fsm phase-and-party-id-from-fsm find-party party-model/role-keys))))})

(def not-only-applicant
  {:name    :process.guard/not-only-applicant
   :type    :guard
   :summary "Don't allow removing this party if it is the only applicant for this application"
   :handler (fn [{{:keys [target]} :request :as fsm}]
              (not= (vec (map :db/id (party-model/active-parties-for-the-role target :role/applicant)))
                    [(:db/id (-> fsm phase-and-party-id-from-fsm find-party))]))})

(def locked-party-details-when-signed-off-except
  {:name    :process.guard/locked-party-details-when-signed-off-except
   :type    :guard
   :summary "Returns false if the target party currently has some of the sign off roles.
             Roles given as guard parameter are excluded from the checks.
             Used to block editing party details and deleting the party."
   :handler (fn [{[_ unrestricted-roles] ::tk/guard :as fsm}]
              (check-locked-party-when-signed-off
                fsm (-> fsm phase-and-party-id-from-fsm find-party party-model/role-keys) unrestricted-roles))})

(def locked-party-roles-when-signed-off
  {:name    :process.guard/locked-party-roles-when-signed-off
   :type    :guard
   :summary "Returns false if the signal tries to add/remove a role, add/invite a party with a role, or add
             a requirement for a role for which sign off is required in the configurations and some sign
             off exists already.
             Used to block adding and removing of roles that are included in the sign-off document."
   :handler (fn [fsm]
              (check-locked-party-when-signed-off fsm (role-keys-from-fsm-role-id fsm)))})

(def party-enabled?
  {:name    :process.guard/party-enabled?
   :type    :guard
   :summary "Has the party not already been marked removed?"
   :handler (fn [fsm]
              (-> fsm phase-and-party-id-from-fsm find-party party-model/removed? not))})

(def party-of-user?
  {:name    :process.guard/party-of-user?
   :type    :guard
   :summary "Is the party of the signal (:party-id) one the current user can act as?"
   :handler (fn [{root-user :core.user/user :as fsm}]
              ;; Backend FSMs include the user both at the root and in :request, while frontend
              ;; FSMs only include it at the root.
              (let [user (or root-user (-> fsm :request :core.user/user))]
                (->> fsm
                     phase-and-party-id-from-fsm
                     find-party
                     (party-model/user-can-act-as-party? user)
                     boolean)))})

(def user-can-act-as-party?
  {:name    :process.guard/user-can-act-as-party?
   :type    :guard
   :summary "Can the current user control the party specified by the key given as guard argument?"
   :handler (fn [{[_ party-k]                      ::tk/guard
                  [_ params]                       ::tk/signal
                  {:keys [target :core.user/user]} :request}]
              (p/if-all-let [party-id (-> params (or party-k :party-id))
                             party    (find-party target party-id)]
                (party-model/user-can-act-as-party? user party)
                ;; Return true for empty to allow successful signal check before party selection.
                ;; The action will ultimately crash if called without a party.
                true))})

(def personal-id-valid-if-present?
  {:name    :process.guard/personal-id-valid-if-present?
   :type    :guard
   :summary "Personal ID in specified attr path must match country-specific rules if present."
   :handler (fn [{[_ attr-path]    ::tk/guard
                  [_ params]       ::tk/signal
                  {:keys [target]} :request}]
              (let [attr-path     (if (keyword? attr-path)
                                    (vector attr-path)
                                    attr-path)
                    personal-id   (get-in params attr-path)
                    pid-validator (and personal-id
                                       (-> target
                                           :entity/parent
                                           :workspace/municipality
                                           :municipality/country
                                           form-validators/personal-id-validators))]
                (-> (or (not personal-id)
                        (pid-validator personal-id))
                    boolean)))})

(def guards
  [application-has-applicant?
   no-user-applicant-parties
   required-party-roles
   required-party-roles-except-deferred
   required-party-role-trades
   party-role-not-required?
   party-has-user-without-integration?
   locked-party-details-when-signed-off
   locked-party-details-when-signed-off-except
   locked-party-roles-when-signed-off
   party-has-role?
   party-has-no-sacred-roles?
   party-has-other-roles?
   single-party-of-type
   single-business-party-of-type
   unique-party-email?
   unique-business?
   party-has-email-but-no-user?
   party-enabled?
   party-of-user?
   user-can-act-as-party?
   personal-id-valid-if-present?
   not-only-applicant])
